import type { CartPayment as CartPaymentType } from '@eliadmin/core/src/api/cart/cart-types';
import {
  CUSTOMER_CREDIT_TYPES,
  sumCustomerCreditBalances,
} from '@eliadmin/core/src/api/customers/customer-credit-utils';
import { fetchMe } from '@eliadmin/core/src/api/customers/customers-api';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { Switch } from '@eliadmin/core/src/components/ui/switch';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { JobportalPaymentCard } from './jobportal-payment-card';
import { trackEcommerce } from '../analytics/analytics-helper';
import { payOffer } from '@eliadmin/core/src/api/jobportal/orders-api';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { getJobPortalPaymentMethods } from '../../utils/jobportal-payment-methods';
import {
  getJobOfferPaymentReturnUrl,
  redirectJobOfferPayment,
} from '../../utils/job-offer-payment';

type JobOfferPayOfferDialogProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  offerProduct?: ProductDetailData;
  topProduct?: ProductDetailData;
  payments?: CartPaymentType[];
  slug: string;
  isDual?: boolean;
  onSuccess?: () => void;
  onLater?: () => void;
  offerSlug?: string;
};

export const JobOfferPayOfferDialog = (props: JobOfferPayOfferDialogProps) => {
  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const locale = useLocaleStore(s => s.currentLocale);
  const { me: meLocal } = useCustomerStore();
  const me = useQuery({
    queryKey: ['me', locale, meLocal?.authToken ?? null],
    queryFn: () => fetchMe({ locale, authToken: meLocal?.authToken || '' }),
    enabled: props.isOpen && !!meLocal?.authToken,
  }).data;
  const [selectedPayment, setSelectedPayment] = useState<string>(
    props.payments?.[0]?.id?.toString?.() || '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionPending = useRef(false);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const freeOffers = sumCustomerCreditBalances(me?.data ?? meLocal?.customer, [
    CUSTOMER_CREDIT_TYPES.OFFER_BALANCE_FREE,
    CUSTOMER_CREDIT_TYPES.OFFER_BALANCE,
  ]);
  const freeTopOffers = sumCustomerCreditBalances(me?.data ?? meLocal?.customer, [
    CUSTOMER_CREDIT_TYPES.OFFER_TOPPING_BALANCE_FREE,
    CUSTOMER_CREDIT_TYPES.OFFER_TOPPING_BALANCE,
  ]);
  // A null preference follows asynchronously loaded balances without overwriting a user's choice.
  const [offerCreditPreference, setUseFreeBalanceOffer] = useState<boolean | null>(null);
  const useFreeBalanceOffer = !props.isDual && freeOffers > 0 && (offerCreditPreference ?? true);
  const [topOffer, setTopOffer] = useState(false);
  const [topCreditPreference, setUseFreeTopBalance] = useState<boolean | null>(null);
  const useFreeTopBalance = freeTopOffers > 0 && (topCreditPreference ?? true);
  const [selectedVariantId, setSelectedVariantId] = useState(props.isDual ? 10 : 3);

  useEffect(() => {
    if (props.isDual) {
      setSelectedVariantId(10);
    } else {
      setSelectedVariantId(3);
    }
  }, [props.isDual]);

  // Use offerSlug if provided, otherwise fall back to slug
  const offerSlug = props.offerSlug || props.slug;

  // Extract ALL topping variants from the top product (not just filtered ones)
  const allTopVariants = useMemo(
    () => props.topProduct?.variants || [],
    [props.topProduct?.variants],
  );
  const [selectedTopVariant, setSelectedTopVariant] = useState<string>('');

  const variant = props.offerProduct?.variants?.find(variant => variant.id === selectedVariantId);
  const variantPriceDisplay = variant ? getProductPriceDisplay(variant) : null;
  const variantCanBePurchased = !!variantPriceDisplay && !variantPriceDisplay.priceOnRequest;
  const selectedTopVariantData = allTopVariants.find(
    currentVariant => String(currentVariant?.id) === selectedTopVariant,
  );
  const selectedTopVariantPriceDisplay = selectedTopVariantData
    ? getProductPriceDisplay(selectedTopVariantData)
    : null;
  const selectedTopVariantCanBePurchased =
    !topOffer ||
    (!!selectedTopVariantPriceDisplay && !selectedTopVariantPriceDisplay.priceOnRequest);
  const canPurchaseSelection = variantCanBePurchased && selectedTopVariantCanBePurchased;

  const { toast } = useToast();

  // Only update topping variant selection if the current selection is not valid
  useEffect(() => {
    if (allTopVariants && allTopVariants.length > 0) {
      const validVariants = allTopVariants.filter(
        currentVariant => !getProductPriceDisplay(currentVariant).priceOnRequest,
      );
      if (validVariants.length > 0) {
        const currentVariant = allTopVariants.find(v => String(v?.id) === selectedTopVariant);
        const isValid =
          currentVariant &&
          validVariants.some(validV => String(validV?.id) === String(currentVariant?.id));
        if (!isValid) {
          setSelectedTopVariant(String(validVariants[0]?.id) || '');
        }
      }
    }
  }, [useFreeTopBalance, allTopVariants, selectedTopVariant]);

  // The configured variant price is authoritative, including dual education.
  const orderTotal =
    (!useFreeBalanceOffer && variantCanBePurchased ? (variantPriceDisplay?.current ?? 0) : 0) +
    (topOffer &&
    selectedTopVariant &&
    !useFreeTopBalance &&
    selectedTopVariantPriceDisplay &&
    !selectedTopVariantPriceDisplay.priceOnRequest
      ? (selectedTopVariantPriceDisplay.current ?? 0)
      : 0);

  const availablePayments = useMemo(
    () => getJobPortalPaymentMethods(props.payments, orderTotal),
    [props.payments, orderTotal],
  );

  // Update payment selection when available payments change.
  useEffect(() => {
    setSelectedPayment(current =>
      availablePayments?.some(payment => String(payment.id) === current)
        ? current
        : availablePayments?.[0]?.id?.toString() || '',
    );
  }, [availablePayments]);

  const handlePayForOffer = (free = false) => {
    if (
      !props.offerProduct?.slug ||
      !variant ||
      !canPurchaseSelection ||
      !meLocal?.authToken ||
      !props.isOpen ||
      !availablePayments?.some(payment => String(payment.id) === selectedPayment) ||
      isSubmitting ||
      submissionPending.current
    )
      return;
    submissionPending.current = true;
    setIsSubmitting(true);
    payOffer({
      offerSlug: offerSlug,
      authToken: meLocal?.authToken || '',
      body: {
        payment_type: { id: Number(selectedPayment) },
        variants: [
          { id: variant.id },
          ...(topOffer && selectedTopVariant ? [{ id: parseInt(selectedTopVariant) }] : []),
        ],
        offer_topping_balance_free: !!(topOffer && useFreeTopBalance),
        offer_balance_free: !!(free && useFreeBalanceOffer),
        return_url: getJobOfferPaymentReturnUrl(ROUTES.ORDER_CONFIRM()),
      },
    })
      .then(response => {
        setIsSubmitting(false);
        submissionPending.current = false;
        void queryClient.invalidateQueries({ queryKey: ['me'] });
        try {
          const transaction_id = `offer-${offerSlug}-${Date.now()}`;
          const items: {
            item_id: string;
            item_name: string;
            price: number;
            quantity: number;
            item_brand: string;
            item_category: string;
            google_business_vertical: string;
            id: string;
          }[] = [];
          if (variant) {
            items.push({
              item_id: `offer-${variant.id}`,
              item_name: props.offerProduct?.name || 'Offer',
              price: useFreeBalanceOffer ? 0 : (variantPriceDisplay?.current ?? 0),
              quantity: 1,
              item_brand: 'job-interview',
              item_category: 'offers',
              google_business_vertical: 'retail',
              id: `offer-${variant.id}`,
            });
          }
          if (topOffer && selectedTopVariant) {
            items.push({
              item_id: `offer-top-${selectedTopVariant}`,
              item_name: props.topProduct?.name || 'Offer Top',
              price: useFreeTopBalance ? 0 : (selectedTopVariantPriceDisplay?.current ?? 0),
              quantity: 1,
              item_brand: 'job-interview',
              item_category: 'offers',
              google_business_vertical: 'retail',
              id: `offer-top-${selectedTopVariant}`,
            });
          }
          trackEcommerce.purchase({
            identifier: transaction_id,
            price: items.reduce((sum, it) => sum + (it.price || 0), 0),
            currency,
            price_without_vat: items.reduce((sum, it) => sum + (it.price || 0), 0),
            shipment_price_with_vat: 0,
            items,
            affiliation: '',
            coupon: '',
          });
        } catch {
          // ignore analytics errors
        }
        toast({ title: t('text352'), variant: 'success' });
        props.onSuccess?.();
        props.setIsOpen(false);
        redirectJobOfferPayment(response);
      })
      .catch(e => {
        setIsSubmitting(false);
        submissionPending.current = false;
        toast({
          title: t('text1472'),
          description: (e as any).message,
          variant: 'destructive',
        });
      });
  };

  const handlePayLater = () => {
    props.onLater?.();
    props.setIsOpen(false);
  };

  return (
    <ResponsiveDialog open={props.isOpen} onOpenChange={props.setIsOpen}>
      <ResponsiveDialogContent className="z-[9999] flex max-h-[80vh] flex-col gap-4 overflow-y-auto px-4">
        {isSubmitting ? (
          <div className="flex h-20 items-center justify-center">
            <Spinner className="h-12 w-12 text-secondary" />
          </div>
        ) : (
          <>
            <ResponsiveDialogHeader>
              <div className="mb-4 max-w-full truncate break-words border-b border-gray-200 pb-2 text-center text-2xl font-extrabold tracking-tight text-primary">
                {t('text2124')}
              </div>
            </ResponsiveDialogHeader>
            {/* Free Offer Credits - Only show/use for non-dual offers */}
            {!props.isDual && !!(freeOffers && freeOffers > 0) && (
              <div className="flex flex-col gap-2 rounded-lg border bg-green-50 p-4">
                <div className="flex items-center gap-3">
                  <Switch
                    classNames={{
                      wrapper: cn(
                        'sm:h-[29px] sm:w-14',
                        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-300',
                      ),
                      thumb: cn(
                        'sm:h-6 sm:w-6 bg-white transition-all duration-200 ease-in-out',
                        'sm:data-[state=checked]:translate-x-7',
                      ),
                    }}
                    checked={useFreeBalanceOffer}
                    onCheckedChange={setUseFreeBalanceOffer}
                  />
                  <div className="flex flex-col">
                    <p className="font-semibold text-green-800">{t('text2130')}</p>
                    <p className="text-sm text-green-700">
                      {t('text2131')} {freeOffers}{' '}
                      {freeOffers === 1 ? 'kredit' : freeOffers < 5 ? 'kredity' : 'kreditov'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Main offer variant selection */}
            {props.offerProduct?.variants && (
              <div className="flex flex-col gap-2">
                <RadioGroup
                  value={String(selectedVariantId)}
                  onValueChange={value => setSelectedVariantId(parseInt(value))}
                >
                  {props.offerProduct.variants
                    .filter(variant => {
                      if (!variant) return false;
                      if (props.isDual) {
                        return variant.id === 10;
                      } else {
                        return variant.id !== 10;
                      }
                    })
                    .map(variant => {
                      if (!variant) return null;
                      const currentVariantPrice = getProductPriceDisplay(variant);
                      return (
                        <Label
                          key={variant.id}
                          htmlFor={`offer-variant-${variant.id}`}
                          className={cn(
                            'flex cursor-pointer items-center justify-between rounded-lg border p-4',
                            'job-interview-payment-card',
                          )}
                        >
                          <div className="flex items-center gap-4">
                            <RadioGroupItem
                              id={`offer-variant-${variant.id}`}
                              value={String(variant.id)}
                              disabled={currentVariantPrice.priceOnRequest}
                            />
                            <div>
                              <div className="text-sm font-bold">{props.offerProduct?.name}</div>
                              <p className="flex flex-col text-xs font-semibold">
                                {variant.id === 3
                                  ? '1 mesiac'
                                  : variant.id === 24
                                    ? '2 mesiace'
                                    : ''}
                                {props.isDual && 'Duálne vzdelávanie'}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm font-bold">
                            {useFreeBalanceOffer
                              ? 'ZDARMA'
                              : currentVariantPrice.priceOnRequest
                                ? t('text2278')
                                : `${formatPrice(currentVariantPrice.current ?? 0, currency)} bez DPH`}
                          </span>
                        </Label>
                      );
                    })}
                </RadioGroup>
              </div>
            )}

            {allTopVariants?.length ? (
              <div className="flex flex-col gap-4 pb-1">
                <div className="flex items-center gap-3">
                  <Switch
                    classNames={{
                      wrapper: cn(
                        'sm:h-[29px] sm:w-14',
                        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-300',
                      ),
                      thumb: cn(
                        'sm:h-6 sm:w-6 bg-white transition-all duration-200 ease-in-out',
                        'sm:data-[state=checked]:translate-x-7',
                      ),
                    }}
                    checked={topOffer}
                    onCheckedChange={setTopOffer}
                  />
                  <p className="font-semibold">{t('Zvýrazniť')}</p>
                </div>
                {/* Free Top Credits - Force users to use them when available */}
                {!!(topOffer && freeTopOffers && freeTopOffers > 0) && (
                  <div className="flex flex-col gap-2 rounded-lg border bg-green-50 p-4">
                    <div className="flex items-center gap-3">
                      <Switch
                        classNames={{
                          wrapper: cn(
                            'sm:h-[29px] sm:w-14',
                            'data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-300',
                          ),
                          thumb: cn(
                            'sm:h-6 sm:w-6 bg-white transition-all duration-200 ease-in-out',
                            'sm:data-[state=checked]:translate-x-7',
                          ),
                        }}
                        checked={useFreeTopBalance}
                        onCheckedChange={setUseFreeTopBalance}
                      />
                      <div className="flex flex-col">
                        <p className="font-semibold text-green-800">{'Využiť voľné zvýraznenie'}</p>
                        <p className="text-sm text-green-700">
                          {t('text2131')} {freeTopOffers}{' '}
                          {freeTopOffers === 1
                            ? 'kredit'
                            : freeTopOffers < 5
                              ? 'kredity'
                              : 'kreditov'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Show Platba kreditmi: ZDARMA when using free credits for topping, below the topping section */}
                {/* {topOffer && useFreeTopBalance && (
                  <div className="mt-2 flex items-center justify-between rounded-lg border bg-green-50 p-4">
                    <span className="text-lg font-semibold text-green-800">Platba kreditmi:</span>
                    <span className="text-xl font-bold text-green-600">ZDARMA</span>
                  </div>
                )} */}

                <div
                  className={cn(
                    'overflow-hidden transition-all duration-200',
                    topOffer ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
                  )}
                >
                  <div className="flex flex-col gap-2">
                    <RadioGroup value={selectedTopVariant} onValueChange={setSelectedTopVariant}>
                      {allTopVariants.map(variant => {
                        if (!variant) return null;
                        const currentVariantPrice = getProductPriceDisplay(variant);
                        return (
                          <Label
                            key={variant.id}
                            htmlFor={`top-variant-${variant.id}`}
                            className={cn(
                              'flex cursor-pointer items-center justify-between gap-2 rounded-lg border p-4',
                              'job-interview-payment-card',
                            )}
                          >
                            <div className="flex items-center gap-4">
                              <RadioGroupItem
                                id={`top-variant-${variant.id}`}
                                value={String(variant.id)}
                                disabled={currentVariantPrice.priceOnRequest}
                              />
                              <div className="flex flex-col gap-1">
                                <div className=" text-sm font-bold sm:max-w-[280px]">
                                  {props.topProduct?.name}
                                </div>
                                <p className="text-xs font-semibold">
                                  {variant.id === 1
                                    ? '+ IMMEDIATE JOB'
                                    : variant.id === 2
                                      ? '+ DREAM JOB'
                                      : ''}
                                </p>
                              </div>
                            </div>
                            <span className="whitespace-nowrap text-sm font-bold">
                              {useFreeTopBalance
                                ? 'ZDARMA'
                                : currentVariantPrice.priceOnRequest
                                  ? t('text2278')
                                  : `${formatPrice(currentVariantPrice.current ?? 0, currency)} bez DPH`}
                            </span>
                          </Label>
                        );
                      })}
                    </RadioGroup>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Payment methods - only show when not using free credits */}
            <div className={cn('transition-all duration-200')}>
              {availablePayments && availablePayments.length > 0 && (
                <>
                  <p className="text-lg font-semibold">{t('text348')}</p>
                  <RadioGroup value={selectedPayment} onValueChange={setSelectedPayment}>
                    {availablePayments.map(payment => (
                      <JobportalPaymentCard key={payment.id} payment={payment} />
                    ))}
                  </RadioGroup>
                </>
              )}
            </div>

            {/* Order Total or Free Info */}
            {canPurchaseSelection && availablePayments.length === 0 && (
              <p role="alert" className="text-sm text-destructive">
                {t('Pre túto objednávku nie je dostupný spôsob platby. Kontaktujte podporu.')}
              </p>
            )}

            <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-4">
              <span className="text-lg font-semibold">Celková suma objednávky:</span>
              <span className="text-xl font-bold text-primary">
                {orderTotal > 0 ? formatPrice(orderTotal, currency) + t('text443') : 'ZDARMA'}
              </span>
            </div>

            <div className="flex w-full justify-between">
              <Button variant="default-alt" onClick={handlePayLater}>
                {t('text2265')}
              </Button>
              <Button
                onClick={() => handlePayForOffer(useFreeBalanceOffer)}
                disabled={
                  !canPurchaseSelection ||
                  !meLocal?.authToken ||
                  !availablePayments?.some(payment => String(payment.id) === selectedPayment)
                }
              >
                {t('text2124')}
              </Button>
            </div>
          </>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
